"use client";
import TeamCards from "@/components/card-rotate";
import { Button } from "@/components/ui/button";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { CustomWiggle, Flip, GSDevTools, ScrollTrigger, CustomEase } from "gsap/all";
import { Menu, MenuIcon } from "lucide-react";
import Link from "next/link";
import { useLayoutEffect, useRef, useState } from "react";
import { motion, useMotionTemplate } from "framer-motion";
import { useLenis } from "lenis/react";
import SmokePage from "./model/path/main";
import Portal from "./model/portal";
gsap.registerPlugin(CustomEase, CustomWiggle, GSDevTools, ScrollTrigger, Flip);
ScrollTrigger.config({ ignoreMobileResize: true });

export const CurvedBorder = () => {
  const root = useRef(null);
  const svgRef = useRef(null);
  const pathRef = useRef(null);

  const height = 700;

  useLayoutEffect(() => {
    const svg = svgRef.current;
    const path = pathRef.current;

    const ctx = gsap.context((self) => {
      let width = svg.getBoundingClientRect().width;

      // Default path
      const getDefaultPath = () => `M 0 ${height / 2} Q ${width / 2} ${height / 2} ${width} ${height / 2}`;

      path.setAttribute("d", getDefaultPath());

      self.add("followMouse", (e) => {
        const rect = svg.getBoundingClientRect();
        width = rect.width; // update width in case window resizes
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const dx = mouseX - width / 2;
        const dy = mouseY - height / 2;

        const offsetX = Math.max(-height, Math.min(dx, height));
        const offsetY = Math.max(-height, Math.min(dy, height));

        const controlX = width / 2 + offsetX * 0.3;
        const controlY = height / 2 + offsetY;

        const newPath = `M 0 ${height / 2} Q ${controlX} ${controlY} ${width} ${height / 2}`;

        gsap.to(path, {
          duration: 0.3,
          attr: { d: newPath },
          ease: "power.Out"
        });
      });

      // Reset animation
      self.add("resetCurve", () => {
        width = svg.getBoundingClientRect().width;
        gsap.to(path, {
          duration: 1.6,
          attr: { d: getDefaultPath() },
          ease: "elastic.out(1, 0.3)"
        });
      });

      // Events
      path.addEventListener("mouseenter", () => {
        svg.addEventListener("mousemove", ctx.followMouse);
      });
      path.addEventListener("touchstart", () => {
        svg.addEventListener("mousemove", ctx.followMouse);
      });
      svg.addEventListener("touchend", () => {
        svg.addEventListener("mousemove", ctx.followMouse);
        ctx.resetCurve();
      });
      svg.addEventListener("mouseleave", () => {
        svg.removeEventListener("mousemove", ctx.followMouse);
        ctx.resetCurve();
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={root} className='absolute left-0 top-0 w-full flex my-20 z-[999]'>
      <svg ref={svgRef} width='100%' height={height} className='z-[999] bg-transparent pointer-events-auto'>
        <path ref={pathRef} stroke='#71717a' strokeWidth='3' fill='transparent' className='hover:stroke-cyan-400 transition-colors z-[999]' style={{ pointerEvents: "stroke", cursor: "pointer" }} />
      </svg>
    </div>
  );
};
const PortoPage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef(null);
  const imageRef = useRef(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const skillsRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const h3 = document.querySelectorAll<HTMLHeadingElement>("h3");
      const h3s = gsap.utils.toArray<HTMLHeadingElement>(h3);

      h3s.forEach((h3) => {
        const text = h3.textContent || "";
        h3.textContent = "";
        h3.classList.add("overflow-hidden", "w-fit");

        // Pecah text jadi huruf
        const chars = text.split("");
        chars.forEach((char) => {
          const span = document.createElement("span");
          span.textContent = char === " " ? "\u00A0" : char;
          span.classList.add("inline-block", "overflow-hidden", "w-fit");
          h3.appendChild(span);
        });

        const spans = h3.querySelectorAll("span");

        // Reset posisi huruf
        gsap.set(spans, { yPercent: 0, opacity: 1 });

        // Hover masuk
        h3.addEventListener("mouseenter", () => {
          const tl = gsap.timeline();

          // Huruf naik ke atas (keluar)
          tl.fromTo(
            spans,
            {
              yPercent: 0,
              opacity: 1,
              delay: 1.5
            },
            {
              yPercent: -100,
              opacity: 1,
              stagger: { each: 0.02, from: "end", axis: "y" },
              duration: 1.3,
              ease: "power(0,1)"
            }
          ).fromTo(
            spans,
            { yPercent: 100, opacity: 1 },
            {
              yPercent: 0,
              opacity: 1,

              stagger: { each: 0.02, amount: 0.5, from: "end", ease: "sine" },
              duration: 1.3,
              ease: "circ"
            },
            "<=0.15"
          );
        });

        // Hover keluar → balik normal (tanpa animasi ribet)
      });
      // ScrollTrigger.normalizeScroll({
      //   allowNestedScroll: true,
      //   // type: "touch,wheel,pointer",
      //   lockAxis: true
      // });
      const btn = btnRef.current;
      if (!btn) return;

      const move = (e: MouseEvent) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        gsap.to(btn, {
          x: x * 0.3,
          y: y * 0.3,
          duration: 0.3,
          ease: "power3.out"
        });
        gsap.to(".btn-text", {
          x: x * 0.2,
          y: y * 0.4,
          duration: 0.5,
          ease: "power3.out"
        });
      };

      const reset = () => {
        gsap.to([btn, ".btn-text"], {
          x: 0,
          y: 0,
          duration: 0.5,
          ease: "elastic.out(1, 0.4)"
        });
      };

      gsap.to(btn, {
        keyframes: [
          { rotate: 8, x: 5, duration: 0.1 },
          { rotate: -8, x: -5, duration: 0.1 },
          { rotate: 6, x: 4, duration: 0.1 },
          { rotate: -6, x: -4, duration: 0.1 },
          { rotate: 0, x: 0, duration: 0.2 }
        ],
        ease: "power1.inOut",
        repeat: -1,
        repeatDelay: 2
      });
      const hoverIn = () => {
        gsap.to(".btn-bg", {
          clipPath: "circle(150% at 0% 50%)",
          duration: 0.8,
          ease: "elastic.out(1, 0.5)"
        });
        gsap.to(".btn-text", {
          color: "#fff",
          duration: 0.4,
          ease: "power2.out"
        });
      };

      const hoverOut = () => {
        gsap.to(".btn-bg", {
          clipPath: "circle(0% at 0% 50%)",
          duration: 0.6,
          ease: "power3.inOut"
        });
        gsap.to(".btn-text", {
          color: "#000",
          duration: 0.4,
          ease: "power2.inOut"
        });
      };
      btn.addEventListener("mousemove", move);
      btn.addEventListener("mouseleave", reset);
      btn.addEventListener("mouseenter", hoverIn);
      btn.addEventListener("mouseleave", hoverOut);

      return () => {
        btn.removeEventListener("mousemove", move);
        btn.removeEventListener("mouseleave", reset);
        btn.removeEventListener("mouseenter", hoverIn);
        btn.removeEventListener("mouseleave", hoverOut);
      };
    },
    { scope: btnRef }
  );
  const lenis = useLenis();
  useGSAP(
    () => {
      const dataPanelSection = gsap.utils.toArray<HTMLElement>("[data-panel-section]");
      const sections = document.querySelectorAll("section");

      gsap.to(
        ".text-rtl",

        {
          x: -1000,
          scrollTrigger: {
            scrub: true,
            trigger: ".text-rtl",
            start: "top bottom",
            end: "bottom top"
            // markers: true
          }
        }
      );
      gsap.to(".imageBg", {
        y: "-10%",
        scrollTrigger: {
          trigger: ".imageBg",
          start: "top bottom",
          end: "bottom top",
          // markers: true,
          scrub: true
        }
      });
      gsap.fromTo(
        ".text-p-hero",
        { yPercent: 100 },
        {
          yPercent: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".text-p-hero",
            start: "top bottom",
            end: "bottom top",
            // markers: true,
            toggleActions: "play none none reverse"
          }
        }
      );
      gsap.fromTo(
        ".brands",
        { clipPath: "inset(0 100% 0 0)" },
        {
          clipPath: "inset(0 0% 0 0)",
          duration: 1.5,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".text-p-hero",
            start: "top 80%",
            end: "bottom top",
            toggleActions: "play none none reverse"
          }
        }
      );
      sections.forEach((section, i) => {
        const color = i === 0 ? "transparent" : i === 1 ? "skyblue" : i === 2 ? "#2903FF" : i === 3 ? "#FCAD02" : "red";

        const hideHeader = () =>
          gsap.set(headerRef.current, {
            opacity: 0,
            visibility: "hidden",
            pointerEvents: "none",
            duration: 0.4,
            ease: "power2.out"
          });

        const showHeader = (bg: string) =>
          gsap.to(headerRef.current, {
            opacity: 1,
            visibility: "visible",
            backgroundColor: bg,
            pointerEvents: "auto",
            duration: 0.4,
            ease: "power2.out"
          });

        ScrollTrigger.create({
          trigger: section,
          start: "bottom top",
          end: "top top",
          scrub: true,
          onRefreshInit: () => (color === "transparent" ? hideHeader() : showHeader(color)),
          onEnter: () => (color === "transparent" ? hideHeader() : showHeader(color)),
          onEnterBack: () => (color === "transparent" ? hideHeader() : showHeader(color))
        });
      });

      gsap.fromTo(
        imageRef.current,
        {
          opacity: 1,
          clipPath: "inset(0 0 100% 0)"
        },
        {
          opacity: 1,
          clipPath: "inset(0 0 0% 0)",
          ease: "power3.inOut",
          scrollTrigger: {
            trigger: imageRef.current,
            toggleActions: "play none none reverse",
            start: "top center",
            end: "bottom center",
            invalidateOnRefresh: true
          }
        }
      );
      dataPanelSection.forEach((section, i) => {
        const bg = section.querySelector<HTMLElement>(".bg-layer");
        const overlay = document.querySelector(".overlay-shadow");

        if (overlay) {
          gsap.to(overlay, {
            opacity: 0.86,
            ease: "none",
            scrollTrigger: {
              trigger: dataPanelSection[0],
              invalidateOnRefresh: true,
              start: "top top",
              end: "bottom top",
              scrub: true
            }
          });
        }
        gsap.fromTo(
          bg,
          { scale: 1.2, y: 0 },
          {
            scale: 1,
            y: -100,
            ease: "none",
            scrollTrigger: {
              invalidateOnRefresh: true,
              trigger: section,
              start: "top bottom",
              end: "bottom top",
              scrub: true
            }
          }
        );

        // Section akhir sudah fixed permanen di markup — tidak perlu di-pin
        // maupun dianimasi posisinya (keduanya menyebabkan flicker).
        if (section.dataset.panelSection === "end") return;

        ScrollTrigger.create({
          trigger: section,
          start: "top top",
          end: "bottom top",
          invalidateOnRefresh: true,
          pin: true,
          pinSpacing: false
        });
      });

      // Sinkronkan Lenis smooth-scroll dengan ScrollTrigger agar pin tidak bergetar
      const onLenisScroll = () => ScrollTrigger.update();
      lenis?.on("scroll", onLenisScroll);

      return () => {
        lenis?.off("scroll", onLenisScroll);
        ScrollTrigger.getAll().forEach((st) => st.kill());
      };
    },
    { scope: containerRef }
  );

  useGSAP(
    () => {
      // ── BG TEXT PARALLAX ──
      gsap.fromTo(
        ".skills-bg-text",
        { yPercent: 24, xPercent: -8 },
        {
          yPercent: -24,
          xPercent: 8,
          ease: "none",
          scrollTrigger: {
            trigger: skillsRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        }
      );

      // ── HEADER ──
      gsap
        .timeline({
          scrollTrigger: {
            trigger: ".skills-header",
            start: "top 75%",
            once: true
          }
        })
        .fromTo(".skills-eyebrow-line", { scaleX: 0 }, { scaleX: 1, transformOrigin: "left center", duration: 0.7, ease: "power3.out" }, 0)
        .fromTo(".skills-eyebrow-text", { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0.2)
        .fromTo(".skills-word-inner", { yPercent: 115 }, { yPercent: 0, stagger: 0.14, duration: 1, ease: "power4.out" }, 0.25)
        .fromTo(".skills-desc", { opacity: 0, y: 24, filter: "blur(6px)" }, { opacity: 1, y: 0, filter: "blur(0px)", duration: 0.9, ease: "power3.out" }, 0.7);

      // ── CARDS ENTRANCE ──
      gsap.fromTo(
        ".skill-card",
        { y: 90, opacity: 0, rotateX: -12, transformOrigin: "50% 100%" },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          stagger: 0.09,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".skills-grid",
            start: "top 72%",
            once: true
          }
        }
      );

      // ── CARD NUMBER COUNT-UP ──
      const nums = gsap.utils.toArray<HTMLElement>(".skill-num");
      nums.forEach((el) => {
        const end = parseInt(el.textContent || "0", 10);
        const obj = { v: 0 };
        gsap.to(obj, {
          v: end,
          duration: 1.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            once: true
          },
          onUpdate: () => {
            el.textContent = String(obj.v).padStart(2, "0");
          }
        });
      });

      // ── QUOTE ──
      gsap.fromTo(
        ".skills-quote",
        { opacity: 0, filter: "blur(8px)" },
        {
          opacity: 1,
          filter: "blur(0px)",
          duration: 1.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".skills-quote",
            start: "top 90%",
            once: true
          }
        }
      );

      // ── CARD HOVER MICRO-INTERACTION ──
      const cards = gsap.utils.toArray<HTMLElement>(".skill-card");
      const cleanup: Array<() => void> = [];
      cards.forEach((card) => {
        const onEnter = () => {
          gsap.to(card, { y: -8, duration: 0.45, ease: "power3.out" });
          gsap.to(card.querySelector(".skill-num"), { scale: 1.15, duration: 0.4, ease: "back.out(3)" });
        };
        const onLeave = () => {
          gsap.to(card, { y: 0, duration: 0.7, ease: "elastic.out(1, 0.45)" });
          gsap.to(card.querySelector(".skill-num"), { scale: 1, duration: 0.5, ease: "power2.out" });
        };
        card.addEventListener("mouseenter", onEnter);
        card.addEventListener("mouseleave", onLeave);
        cleanup.push(() => {
          card.removeEventListener("mouseenter", onEnter);
          card.removeEventListener("mouseleave", onLeave);
        });
      });

      return () => cleanup.forEach((fn) => fn());
    },
    { scope: skillsRef },
  );

      useGSAP(
        () => {
          gsap.fromTo(
            ".expertise-title",
            { clipPath: "inset(0 100% 0 0)", y: 30 },
            {
              clipPath: "inset(0 0% 0 0)",
              y: 0,
              duration: 1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: "#expertise",
                start: "top 75%",
                toggleActions: "play none none reverse"
              }
            }
          );
          gsap.fromTo(
            ".expertise-item",
            { y: 60, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.9,
              ease: "power3.out",
              stagger: 0.12,
              scrollTrigger: {
                trigger: "#expertise",
                start: "top 65%",
                toggleActions: "play none none reverse"
              }
            }
          );
        },
        { scope: containerRef }
      );

      useGSAP(
        () => {
          const tl = gsap.timeline({ defaults: { ease: "power4.out" } });
      tl.fromTo(
        ".hero-img-bg",
        { scale: 1.2, opacity: 0 },
        { scale: 1, opacity: 1, duration: 1.4, ease: "power3.out" },
        0
      )
        .fromTo(".hero-name", { yPercent: 115 }, { yPercent: 0, duration: 1.2, ease: "power4.inOut" }, 0.3)
        .fromTo(
          ".hero-title",
          { clipPath: "inset(0 0 100% 0)", y: 60 },
          { clipPath: "inset(0 0 0% 0)", y: 0, duration: 1.1 },
          0.6
        )
        .fromTo(".hero-desc", { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, "-=0.4");
    },
    { scope: containerRef }
  );

  return (
    <div ref={containerRef} className='relative font-bebas-neue overflow-hidden'>
      {/* teks utama di layer blend */}
      <div className='fixed top-0 h-52  inset-0  overflow-hidden   translate-y-10 z-[9999]  flex  justify-center mix-blend-difference w-fit  justify-self-center '>
        <h1 className='md:text-[5vw] text-3xl font-extrabold text-white w-fit'>TIMES HIRED</h1>
        <div className='fixed inset-0  -translate-y-0 h-fit overflow-hidden  z-[1] flex  justify-center '>
          <div className='text-[2rem]  sm:text-[10rem] md:text-[7vw] translate-y-0 font-extrabold  backdrop-blend-multiply font-raleway mask-b-from-chart-3  bg-[#00000000] '>0</div>
        </div>
      </div>

      <header ref={headerRef} className='header fixed z-50 top-0 left-0 text-transparent right-0 bg-clip-text  w-fit inset-x-10 text-4xl '>
        <div className='md:p-10  w-full   text-center flex flex-col pt-96    '>
          <Link href='/' className=' flex  h-fit w-fit bg-black/50  items-center justify-center py-2 px-4 rounded-full  text-2xl'>
            Go Home
          </Link>
        </div>
      </header>

      <section data-panel-section className='relative  w-full bg-black  h-screen  flex '>
        <div className='inset-x-10 h-full absolute z-[99] border-l-2 border-l-zinc-400 border-r-2 border-r-zinc-400'></div>

        <div className='overlay-shadow h-screen absolute inset-0 bg-black opacity-0 z-50' />
        <div className='hero-title relative z-10    p-5     text-6xl w-full bg-white text-black'>
          <div className='w-full    h-full flex items-start pl-10 sm:leading-25 justify-end flex-col pb-[10vh] text-xl  sm:text-[min(10vw,10vh)] '>
            Digital Designer
            <br />
            <span className='hero-desc text-sm md:text-4xl pb-20   leading-3 md:leading-normal text-neutral-500 text-justify font-normal md:max-w-full pr-5'> I help designer and agencies elevate their brending with creative development support</span>
          </div>
        </div>
        <div className='relative z-10 w-full   text-white text-6xl overflow-hidden'>
          <div
            className='hero-img-bg absolute inset-0'
            style={{
              backgroundImage: `url('/image/me.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center center"
            }}
          ></div>
        </div>
        <div className='absolute z-10 flex items-end justify-center w-full h-full bottom-0 overflow-hidden pointer-events-none'>
          <span className='hero-name inline-block text-amber-50 font-bold uppercase text-[50vw] sm:text-[min(10vw,10vh)] text-nowrap leading-20'>Muh Hilman Humaini</span>
        </div>
      </section>

      <section className='relative bg-white  w-full  overflow-hidden '>
        <div className='borderAnim md:inset-x-10  inset-x-5 h-full border-r-zinc-400  border-r-2 border-l-2 border-l-zinc-400 absolute z-[10]'></div>
        <div
          className='bg-layer imageBg  bg-red-900 absolute opacity-100 bg-blend-darken inset-0  md:inset-15'
          style={{
            backgroundImage: `url('https://i.pinimg.com/736x/01/e8/c9/01e8c9bac0dee479d177a94a4e5f5669.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: "center center"
          }}
        ></div>
        <div className='relative  md:p-15   text-white flex flex-col w-full'>
          <div className='h-full pb-10 text-sm md:text-6xl pt-20'>
            <div className='relative w-full h-full '>
              <img ref={imageRef} src='https://i.pinimg.com/1200x/95/47/27/9547277d7e5f6540b42ae8ff6fd0cc12.jpg' className='md:ml-20 md:mr-0 ml-1  mr-0  md:w-80 w-50 float-right opacity-0' />
            </div>

            <p className='text-p-hero pt-0  md:pt-[25vh] px-10 md:leading-20 text-2xl md:text-7xl    font-semibold'>
              I help <span className='bg-white text-black space-y-0  line-through brands'> brands craft </span>meaningful stories and compelling visuals that deeply resonate with their audience, foster strong connections, build lasting
              loyalty, and drive sustainable long-term growth.
            </p>
          </div>

          <div className='flex flex-col border  place-content-end md:pt-20  md:px-20 bg-black text-black'>
            <CurvedBorder />
            <div className=' 2xl:mx-auto w-fit relative  grid grid-cols-2 p-10  border-t-2   md:pt-8  gap-10'>
              <div className='absolute border-1 w-full border-zinc-400  wigle '></div>

              <div className=' h-fit'>
                <h3 className='text-base md:text-2xl font-semibold mb-4'>From A to Z</h3>
                <p className='md:text-lg text-xs font-sans  text-neutral-500  max-w-64  '>
                  I manage your entire branding process, from concept to execution. Whether it&apos;s logo design, messaging, or strategy, I ensure everything aligns for a cohesive and impactful brand.
                </p>
              </div>
              <div className=' h-fit'>
                <h3 className='text-base md:text-2xl font-semibold mb-4'>Solo or Team</h3>
                <p className='md:text-lg text-xs font-sans  text-neutral-500  max-w-64  '>
                  I work both independently and with a trusted team, adapting to your project’s needs to deliver the best results, whether it&apos;s a solo vision or a collaborative effort.
                </p>
              </div>
              <button ref={btnRef} className=' z-20 relative text-white overflow-hidden w-fit h-fit md:px-12 md:py-5 mb-10 py-2 px-5 rounded-full bg-amber-600  text-base  text-center'>
                <span className='btn-bg absolute inset-0 bg-red-900 text-black  [clip-path:circle(0%_at_10%_50%)]'></span>
                <div className='btn-text text-xs md:text-lg '>More about me</div>
              </button>
            </div>
            <span className='border-t-2 border-zinc-400 '></span>
            <div id='expertise' className='relative px-6 md:px-20 pt-24 pb-32 bg-zinc-100'>
              <div className='max-w-7xl mx-auto'>
                <h2 className='expertise-title text-4xl md:text-6xl font-bold mb-16 tracking-tighter text-zinc-900'>
                  <span className='text-red-600'>/</span> Fields of Expertise
                </h2>

                <div className='grid grid-cols-1 gap-1'>
                  {[
                    {
                      num: "01",
                      title: "High-End Landing Pages",
                      desc: "More than just design — a visual strategy that boosts conversions and strengthens your brand's first impression."
                    },
                    {
                      num: "02",
                      title: "Multi-Page Architecture",
                      desc: "Complex website structures that stay lightweight and easy to navigate, keeping the user flow consistent."
                    },
                    {
                      num: "03",
                      title: "E-Commerce Experience",
                      desc: "Building digital stores that prioritize frictionless transactions without sacrificing visual aesthetics."
                    },
                    {
                      num: "04",
                      title: "UI/UX App Design",
                      desc: "Mobile and web app design centered on user behavior, ensuring intuitive functionality."
                    },
                    {
                      num: "05",
                      title: "UX Audit & Optimization",
                      desc: "Analyzing friction points in your product and delivering technical solutions for better performance."
                    }
                  ].map((item, idx) => (
                    <div key={idx} className='expertise-item group grid grid-cols-[auto_1fr] gap-8 md:gap-16 py-8 border-b border-zinc-300 hover:bg-zinc-200/50 transition-colors px-4 -mx-4'>
                      <span className='text-3xl md:text-5xl font-mono text-zinc-400 group-hover:text-red-600 transition-colors'>{item.num}</span>
                      <div className='space-y-2'>
                        <h3 className='text-3xl md:text-5xl font-bold tracking-tight text-zinc-800'>{item.title}</h3>
                        <p className='text-sm md:text-lg text-zinc-500 font-raleway max-w-2xl'>{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section data-panel-section className='relative  w-full h-screen overflow-hidden flex'>
        <div className='md:columns-3 flex flex-wrap md:block h-full w-full  '>
          {/* Kolom 1 */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className='border  w-full  md:h-full h-fit flex flex-col 2xl:flex-row  gap-4 2xl:p-20  bg-blend-color  backdrop-brightness-50 bg-neutral-200/10 '
          >
            <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: false, amount: 0.3 }} className='h-1/2 mt-20   p-4 relative md: md:text-6xl '>
              <motion.div
                initial={{ opacity: 0, rotate: -5, y: 100 }}
                whileInView={{ opacity: 0.05, rotate: -10, y: 0 }}
                transition={{ duration: 1.2 }}
                viewport={{ once: false }}
                className='absolute left-0 top-0 text-rtl z-10 webkit-xxs  leading-tight tracking-widest text-[40rem] w-fit h-fit pointer-events-none select-none'
                style={{ whiteSpace: "nowrap" }}
              >
                PEOPLEPEOPLE
              </motion.div>

              <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: false }} className='text-5xl md:text-7xl  text-red-300   mb-2 relative z-10'>
                Programming Language
              </motion.h2>

              <motion.ul initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }} viewport={{ once: false }} className='list-disc  md:text-5xl md:space-y-10 flex flex-col md:mt-10  relative z-10'>
                <div className='border-b-2  border-zinc-700'>JavaScript / TypeScript</div>
                <div className='border-b-2  border-zinc-700'>Python</div>
                <div className='border-b-2  border-zinc-700'>PHP</div>
                <div className='border-b-2  border-zinc-700'>Go (Golang)</div>
                <div className='border-b-2  border-zinc-700'>SQL</div>
              </motion.ul>
            </motion.div>

            <div className='flex-1 md:visible hidden bg-gray-800/20'></div>
          </motion.div>

          {/* Kolom 2 */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className='border w-full h-fit md:h-full flex  flex-col gap-4 md:p-8 pl-0  bg-neutral-700/20'
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: false }}
              className='md:h-96  2xl:items-center 2xl:justyfy-center flex md:flex-col md:pt-52 w-full  p-4'
            >
              <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: false }} className='text-5xl md:text-7xl  text-red-300    mt-10 md:mb-2 relative z-10'>
                Framework <span className='text-white/20 '>&</span> Tools
              </motion.h2>
              <motion.ul initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.5 }} viewport={{ once: false }} className='list-disc list-inside text-sm md:text-4xl '>
                <div className='relative md:translate-x-20 -inset-x-20'>React / Next.js</div>
                <div className='relative -inset-x-10'>Tailwind CSS</div>
                <div className='relative md:translate-x-10'>Express</div>
                <div className='relative md:inset-x-0 md:inset-y-8'>Laravel</div>
                <div className='relative md:inset-x-30 md:inset-y-5 -inset-x-10'>Docker</div>
                <div className='relative md:inset-x-20 md:inset-y-20 -inset-x-20'>Git & CI/CD</div>
              </motion.ul>
            </motion.div>

            <div className=' md:visible hidden flex-1 bg-neutral-500/10'></div>
          </motion.div>

          {/* Kolom 3 */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: false, amount: 0.3 }}
            className='border w-full h-full relative z-10 text-white text-6xl flex flex-col md:p-8'
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: false }}
              className=' md:mb-20 absolute left-0  md:left-0 md:pl-2  h-full w-full text-sm md:relative  z-10 '
            >
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: false }}
                className='text-6xl mt-20 md:text-8xl -top-18 md:-top-50  text-nowrap rotated-text absolute text-red-300  md:pt-80  mb-2 md:relative z-10'
              >
                Fokus Keahlian
              </motion.h2>
              <motion.ul
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: false }}
                className='w-full h-fit text-3xl justify-end text-end items-end md:left-1/3 md:top-1/3 md:text-5xl xl:space-y-10 space-y-2  md:absolute md:items-start pr-5  flex flex-col pt-20 md:pt-0'
              >
                <div className='bg-fuchsia-100 text-black w-fit '>Frontend Development</div>
                <div className='bg-fuchsia-100 text-black w-fit '>UI/UX Integration</div>
                <div className='bg-fuchsia-100 text-black w-fit  '>Design System</div>
                <div className='bg-fuchsia-100 text-black w-fit  '>Web Performance</div>
                <div className='bg-fuchsia-100 text-black w-fit  '>Fullstack dengan JS Stack</div>
              </motion.ul>
            </motion.div>

            <div className='md:visible hidden aspect-[2/3] bg-yellow-600'></div>
          </motion.div>
        </div>
      </section>
      <section id='skills' data-panel-section ref={skillsRef} className='skills-section relative w-full min-h-dvh md:min-h-screen flex flex-col justify-center bg-neutral-950 overflow-hidden text-white px-3 md:px-16 py-2 md:py-32'>
        {/* ── DECORATIVE BG ── */}
        <div className='pointer-events-none absolute inset-0'>
          <div className='skills-bg-text absolute -top-4 md:top-0 left-1/2 -translate-x-1/2 text-[24vw] md:text-[15vw] font-bebas-neue font-bold leading-none text-white/[0.035] tracking-tight whitespace-nowrap select-none uppercase'>
            Skills &amp; Specializations
          </div>
          <div className='absolute top-1/4 -left-24 w-[420px] h-[420px] bg-red-900/10 blur-[140px] rounded-full' />
          <div className='absolute bottom-1/4 -right-24 w-[380px] h-[380px] bg-neutral-700/10 blur-[120px] rounded-full' />
          <div className='absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-neutral-800 to-transparent opacity-60' />
        </div>

        <div className='relative z-10 max-w-6xl mx-auto w-full space-y-3 md:space-y-24'>
          {/* ── HEADER ── */}
          <div className='skills-header flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 md:gap-8'>
            <div className='space-y-5 max-w-2xl'>
              <div className='skills-eyebrow flex items-center gap-3'>
                <span className='skills-eyebrow-line h-px w-10 md:w-14 bg-red-600' />
                <span className='skills-eyebrow-text text-red-500 font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase'>What I do</span>
              </div>
              <h2 className='font-bebas-neue text-[12vw] leading-[0.9] sm:text-6xl md:text-8xl tracking-tight text-white uppercase'>
                <span className='skills-word block overflow-hidden pb-1'>
                  <span className='skills-word-inner block'>Skills</span>
                </span>
                <span className='skills-word block overflow-hidden pb-1'>
                  <span className='skills-word-inner block text-neutral-500'>&amp; Specializations</span>
                </span>
              </h2>
            </div>
            <p className='skills-desc hidden md:block text-neutral-400 max-w-md text-sm md:text-base leading-relaxed lg:pb-3 font-raleway'>
              My main focus is to create efficient, elegant, and meaningful digital experiences — through the collaboration of design, technology, and logic.
            </p>
          </div>

          {/* ── CARDS ── */}
          <div className='skills-grid grid grid-cols-2 lg:grid-cols-3 gap-2 md:gap-5 [perspective:1200px]'>
            {[
              {
                title: "Frontend Engineering",
                tag: "DEV",
                desc: "Building interactive interfaces with React, Next.js, and Tailwind CSS. Prioritizing performance and consistent UI.",
                tags: ["React", "Next.js", "Tailwind"]
              },
              {
                title: "Backend & Database",
                tag: "API",
                desc: "Designing clean API architectures using Node.js, Prisma, and PostgreSQL.",
                tags: ["Node.js", "Prisma", "PostgreSQL"]
              },
              {
                title: "3D & AR Integration",
                tag: "IMMERSIVE",
                desc: "Delivering immersive experiences with Three.js and WebXR — from product visualization to Augmented Reality interaction.",
                tags: ["Three.js", "WebXR", "GLSL"]
              },
              {
                title: "UI/UX Design System",
                tag: "DESIGN",
                desc: "Combining modular design with user logic to produce intuitive and scalable results.",
                tags: ["Figma", "Design Tokens", "Prototyping"]
              },
              {
                title: "Automation & Workflow",
                tag: "OPS",
                desc: "Enhancing project efficiency through build, deployment, and data management automation using CI/CD.",
                tags: ["CI/CD", "Docker", "Cloud"]
              },
              {
                title: "Problem Solving Mindset",
                tag: "MINDSET",
                desc: "Systematically analyzing technical problem patterns and translating them into simple yet effective solutions.",
                tags: ["Architecture", "Debugging", "Optimization"]
              }
            ].map((skill, i) => (
              <div key={skill.title} className='skill-card group relative rounded-xl md:rounded-2xl border border-neutral-800 bg-neutral-900/40 p-3 md:p-7 overflow-hidden transition-colors duration-500 hover:border-red-600/40'>
                <div className='absolute inset-0 bg-gradient-to-br from-red-600/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
                <div className='absolute -top-10 -right-10 w-24 h-24 rounded-full bg-red-600/10 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500' />
                <div className='relative z-10 flex flex-col h-full gap-3 md:gap-5'>
                  <div className='flex items-center justify-between gap-3'>
                    <span className='skill-num font-mono text-xs md:text-sm text-neutral-500 group-hover:text-red-500 transition-colors duration-300'>0{i + 1}</span>
                    <span className='text-[8px] md:text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 border border-neutral-800 rounded-full px-1.5 md:px-3 py-0.5 md:py-1 group-hover:border-red-600/40 group-hover:text-red-400 transition-colors duration-300'>
                      {skill.tag}
                    </span>
                  </div>
                  <h3 className='text-sm md:text-2xl font-medium text-white'>{skill.title}</h3>
                  <p className='hidden md:block text-sm text-neutral-400 leading-relaxed font-raleway'>{skill.desc}</p>
                  <div className='mt-auto pt-2 flex flex-wrap gap-2'>
                    {skill.tags.map((t) => (
                      <span key={t} className='text-[9px] md:text-[11px] text-neutral-500 bg-neutral-800/60 rounded px-2 md:px-2.5 py-0.5 md:py-1 font-mono group-hover:text-neutral-400 transition-colors duration-300'>
                        {t}
                      </span>
                    ))}
                  </div>
                  <span className='skill-arrow absolute top-6 right-16 translate-x-3 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 text-red-500 font-mono'>&rarr;</span>
                </div>
              </div>
            ))}
          </div>

          {/* ── QUOTE ── */}
          <p className='skills-quote hidden md:flex items-center justify-center gap-4 text-neutral-500 text-xs md:text-sm tracking-[0.25em] uppercase text-center font-mono'>
            <span className='h-px w-8 md:w-12 bg-neutral-800' />
            “Code is not just syntax — it&apos;s how ideas take shape.”
            <span className='h-px w-8 md:w-12 bg-neutral-800' />
          </p>
        </div>
      </section>
      <section data-panel-section className='min-h-screen relative w-full backdrop-blur-xl'>
        <Portal />
        {/* <SmokePage /> */}
        {/* <div className='absolute left-0 top-0 w-full h-full -z-10'>
          <SmokePage />
        </div> */}
      </section>

      <section id='about' className='relative z-20 w-full bg-neutral-950 min-h-screen py-24 px-6 md:px-20 overflow-hidden'>
        {/* Ambient background effects */}
        <div className='absolute top-0 left-0 w-full h-full pointer-events-none'>
          <div className='absolute top-1/4 -left-20 w-[500px] h-[500px] bg-red-900/10 blur-[120px] rounded-full' />
          <div className='absolute bottom-1/4 -right-20 w-[400px] h-[400px] bg-zinc-800/20 blur-[100px] rounded-full' />
        </div>

        <div className='relative z-10 max-w-7xl mx-auto'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start'>
            {/* Content Column */}
            <div className='flex flex-col space-y-16'>
              <div className='space-y-6'>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className='flex items-center gap-4'>
                  <span className='h-[1px] w-12 bg-red-500'></span>
                  <span className='text-red-500 font-mono text-sm tracking-[0.3em] uppercase'>Sense & Logic</span>
                </motion.div>

                <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className='text-6xl md:text-8xl font-bold leading-[0.9] text-white tracking-tighter'>
                  Seeing the <span className='text-zinc-500'>World</span> <br />
                  Through <span className='italic font-serif text-red-600'>Feeling</span>.
                </motion.h2>
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className='space-y-10'>
                <p className='text-2xl md:text-4xl text-zinc-300 font-light leading-snug max-w-2xl'>For me, observing is the purest form of learning. About how people interact with space, emotion, and ever-evolving technology.</p>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-12 pt-8 border-t border-zinc-800'>
                  <div className='space-y-4'>
                    <h4 className='text-white font-bold uppercase tracking-widest text-xs'>Work Philosophy</h4>
                    <p className='text-zinc-500 font-raleway text-base leading-relaxed'>
                      Code is not just a sequence of instructions. It is a bridge. I believe technology should feel invisible — so intuitive that users forget there are thousands of lines of logic behind it.
                    </p>
                  </div>
                  <div className='space-y-4'>
                    <h4 className='text-white font-bold uppercase tracking-widest text-xs'>Aesthetic Exploration</h4>
                    <p className='text-zinc-500 font-raleway text-base leading-relaxed'>From architectural details to shadows on city streets, I search for harmony. That curiosity is what I bring to every pixel and interaction I build.</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Visual Column */}
            <div className='relative h-[500px] md:h-[700px] lg:h-[900px] flex items-center justify-center'>
              <div className='absolute inset-0 bg-radial-gradient from-red-900/5 to-transparent pointer-events-none' />
              <div className='scale-90 md:scale-110 lg:scale-125 transition-transform duration-700'>
                <TeamCards />
              </div>

              {/* Floating label */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className='absolute -bottom-10 right-0 md:right-10 bg-white text-black p-6 md:p-8 rounded-full w-32 h-32 md:w-40 md:h-40 flex items-center justify-center text-center text-xs font-bold uppercase tracking-tighter leading-none z-30 rotate-12 shadow-2xl'
              >
                Human <br /> Centered <br /> Design
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      <section data-panel-section='end' className='relative w-full min-h-fit flex flex-col bg-neutral-950'>
        <div className=' flex xl:flex-row flex-col gap-2 pt-22 z-10 text-white text-base md:text-2xl w-full h-full  '>
          <div className='w-full flex flex-col'>
            <h2 className='md:text-9xl text-7xl w-full'>Contact</h2>
            <div className='grid grid-cols-2 gap-2 flex-1'>
              <div className='flex flex-col  '>
                <h3 className='md:text-5xl text-4xl bg-white text-black w-full px-5 py-4'>Navigation</h3>
                <div className='flex flex-col bg-black text-white px-4 py-3 gap-1 flex-1'>
                  <Link href={"/"} className='hover:text-neutral-400 transition-colors'>Home</Link>

                  <Link href={"#expertise"} className='hover:text-neutral-400 transition-colors'>Expertise</Link>
                  <Link href={"#skills"} className='hover:text-neutral-400 transition-colors'>Skills</Link>
                  <Link href={"#about"} className='hover:text-neutral-400 transition-colors'>About</Link>
                </div>
              </div>
              <div className='flex flex-col'>
                <h3 className='md:text-5xl text-4xl bg-white text-black w-full px-5 py-4'>Social Media</h3>
                <div className='flex flex-col bg-black text-white flex-1'>
                  <div className='flex items-center gap-4 px-4 py-2 hover:bg-white/10 transition-colors'>
                    <Link href='https://www.instagram.com/low_elo.9/' target='_blank' className='relative w-full'>
                      <span className='w-full'>Instagram</span>
                    </Link>
                    <span className='font-mono text-sm text-neutral-500'>01</span>
                  </div>
                  <div className='flex items-center gap-4 px-4 py-2 hover:bg-white/10 transition-colors'>
                    <Link href='https://www.facebook.com/bijikuraps' target='_blank' className='relative w-full'>
                      <span className='w-full'>Facebook</span>
                    </Link>
                    <span className='font-mono text-sm text-neutral-500'>02</span>
                  </div>
                  <div className='flex items-center gap-4 px-4 py-2 hover:bg-white/10 transition-colors'>
                    <Link href='https://www.tiktok.com/@sicsy_09?_t=ZS-90UOEmsLXkm&_r=1' target='_blank' className='relative w-full'>
                      <span className='w-full'>TikTok</span>
                    </Link>
                    <span className='font-mono text-sm text-neutral-500'>03</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='w-full flex flex-col'>
            <h2 className='md:text-9xl text-[26vw] w-full text-end px-6 md:px-12'>Get in Touch</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 bg-white text-black px-6 md:px-12 py-8 md:py-12 gap-6 md:gap-10'>
              <div className='flex flex-col justify-between gap-8'>
                <div className='flex flex-col gap-6'>
                  <p className='text-sm sm:text-lg md:text-2xl lg:text-3xl font-light leading-snug'>If you have an idea for a project & redesign, or you want to collaborate, send me a message. Look forward to working with you!</p>
                  <div className='bg-black text-white p-3 md:p-4 self-start'>
                    <Link href='mailto:muhammadhilmanhumaini@gmail.com' className='underline text-sm md:text-base font-mono'>
                      muhammadhilmanhumaini@gmail.com
                    </Link>
                  </div>
                </div>
                <p className='text-[10px] md:text-xs font-mono text-neutral-500'>
                  Form opens your email app with the message pre-filled.
                </p>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>
        <div className='flex flex-wrap justify-between items-center gap-2 px-6 md:px-12 py-6 border-t border-white/10 text-xs md:text-sm font-mono text-neutral-400'>
          <div>© 2025 hilman.co</div>

          <div>All Rights Reserved</div>
        </div>
      </section>
    </div>
  );
};

const SERVICE_OPTIONS = [
  "High-End Landing Page",
  "Multi-Page Website",
  "E-Commerce Store",
  "UI/UX App Design",
  "UX Audit & Optimization",
  "Other / Custom Project",
] as const;

const ContactForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState<string>("");
  const [message, setMessage] = useState("");
  const [opened, setOpened] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const subject = `[Portfolio] ${service} — ${name}`;
    const body = [
      `Name: ${name}`,
      `Email: ${email}`,
      `Service: ${service}`,
      "",
      "Message:",
      message,
    ].join("\n");
    const mailto = `mailto:muhammadhilmanhumaini@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setOpened(true);
    window.location.href = mailto;
  };

  const inputClass =
    "w-full bg-transparent border border-white/40 focus:border-white outline-none px-3 py-2.5 text-sm text-white placeholder:text-white/40 transition-colors";

  return (
    <form
      onSubmit={handleSubmit}
      className='bg-black text-white p-4 md:p-8 flex flex-col gap-4 font-mono h-full'
      noValidate={false}
    >
      <p className='text-xl md:text-3xl font-light font-sans'>Tell me about your project</p>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <label className='flex flex-col gap-1.5'>
          <span className='text-[10px] uppercase tracking-widest text-white/50'>Name</span>
          <input
            type='text'
            required
            minLength={2}
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Your name'
            className={inputClass}
          />
        </label>

        <label className='flex flex-col gap-1.5'>
          <span className='text-[10px] uppercase tracking-widest text-white/50'>Email</span>
          <input
            type='email'
            required
            maxLength={120}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='you@example.com'
            className={inputClass}
          />
        </label>
      </div>

      <label className='flex flex-col gap-1.5'>
        <span className='text-[10px] uppercase tracking-widest text-white/50'>Service you need</span>
        <select
          required
          value={service}
          onChange={(e) => setService(e.target.value)}
          className={`${inputClass} cursor-pointer [&>option]:text-black`}
        >
          <option value='' disabled>
            Choose a service
          </option>
          {SERVICE_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>

      <label className='flex flex-col gap-1.5'>
        <span className='text-[10px] uppercase tracking-widest text-white/50'>Message</span>
        <textarea
          required
          minLength={10}
          maxLength={4000}
          rows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder='Describe your idea, goals, and timeline...'
          className={`${inputClass} resize-none`}
        />
      </label>

      {opened && (
        <p className='text-xs text-white/60'>Opening your email app — the message is pre-filled, just hit send.</p>
      )}

      <button
        type='submit'
        className='self-start border border-white px-6 py-2.5 text-sm uppercase tracking-widest hover:bg-white hover:text-black transition-colors'
      >
        Send Message
      </button>
    </form>
  );
};

export default PortoPage;
